import { FetchError } from 'api/API';
import { A01, A01Input, A01Output } from 'api/U001';
import { useCallback, useState } from 'react';

type UserIdConfirminationDoRequest = (param: {
  request: A01Input;
  onSuccess?: (response: A01Output) => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => Promise<void>;

export function useUserIdConfirmination(): [UserIdConfirminationDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const userIdConfirmination = async (request: A01Input, cancellation: AbortSignal) => {
    const json = await A01(request, cancellation);
    return json;
  };

  const doRequest = useCallback<UserIdConfirminationDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      setLoading(true);
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      try {
        const result = await userIdConfirmination(request, cancelSignal);
        onSuccess?.(result);
      } catch (err) {
        console.error({ err });
        if (err instanceof FetchError) {
          const errorData = await err.response.json();
          const flooredError = new Error(errorData.message || `HTTP ERROR: ${err.message}`);
          onError?.(flooredError);
          setError(flooredError);
        } else {
          const flooredError = new Error(`unknown error. ${err}`);
          onError?.(flooredError);
          setError(flooredError);
        }
      } finally {
        setLoading(false);
      }
    },
    [userIdConfirmination, setLoading, setError]
  );

  return [doRequest, loading, err];
}
